import { useEffect, useState, useRef, createContext, useContext } from 'react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Layout } from '../components/layout/Layout'
import { ManagedSeo } from '../components/seo/ManagedSeo'
import {
  HORSE_RIDER_ASSESSMENT_START_EVENT,
  HorseRiderAssessment,
} from '../components/start/HorseRiderAssessment'
import { assets } from '../config/assets'
import { AH_ORGANIZATION, SCOTT_PERSON } from '../config/schema'
import { logGaEvent } from '../lib/analytics'
import { buildRidersWayStartUrl } from '../lib/ridersWay'

// Context gives deep-nested app CTAs one consistent, account-first handoff.
const OpenTrialModalContext = createContext<(placement: string) => void>(() => {})
const useOpenTrialModal = () => useContext(OpenTrialModalContext)

function startHomepageAssessment(placement: string): void {
  logGaEvent('assessment_entry_click', { placement })
  window.dispatchEvent(new Event(HORSE_RIDER_ASSESSMENT_START_EVENT))
}


// Shared keyframes
const GLOBAL_STYLES = `
  @keyframes fyspPulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.35; }
  }
  .ah-hero-recognition {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    max-width: 640px;
    margin: 0 auto 22px;
  }
  @media (max-width: 560px) {
    .ah-hero-recognition {
      grid-template-columns: 1fr;
      gap: 8px;
      margin-bottom: 22px;
    }
  }
`

export function HomePage() {
  useEffect(() => {
    const sent = new Set<number>()
    let frame = 0

    function measureScroll(): void {
      frame = 0
      const available = document.documentElement.scrollHeight - window.innerHeight
      const percent = available <= 0 ? 100 : Math.round((window.scrollY / available) * 100)
      for (const threshold of [25, 50, 75, 90]) {
        if (percent >= threshold && !sent.has(threshold)) {
          sent.add(threshold)
          logGaEvent('scroll_depth', { page_name: 'homepage', percent: threshold })
        }
      }
    }

    function onScroll(): void {
      if (!frame) frame = window.requestAnimationFrame(measureScroll)
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    // First measurement on the next frame, not synchronously on mount: reading
    // scrollHeight right after commit forces a full layout while the page is
    // still settling (Lighthouse "forced reflow", Sep 2026).
    frame = window.requestAnimationFrame(measureScroll)
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (frame) window.cancelAnimationFrame(frame)
    }
  }, [])

  const openTrialModal = (placement: string) => {
    logGaEvent('riders_way_trial_click', {
      placement,
      destination: 'riders_way_account',
    })
    window.location.assign(buildRidersWayStartUrl(placement))
  }
  return (
    <Layout>
      <ManagedSeo path="/" jsonLd={[AH_ORGANIZATION, SCOTT_PERSON]} />
      <style>{GLOBAL_STYLES}</style>
      <OpenTrialModalContext.Provider value={openTrialModal}>
        <HeroSection />
        <ProblemSection />
        <HorseRiderAssessment />
        <ReadyDeeperSection />
        <TestimonialsSection />
        <AboutScottSection />
        <PhoneShowcaseSection />
        <PillarsSection />
        <RepeatCtaSection />
        <EmailCaptureSection />
      </OpenTrialModalContext.Provider>
    </Layout>
  )
}

// Shared button style for all App trial CTAs.
function TrialButton({
  children,
  placement,
  style,
}: {
  children: ReactNode
  placement: string
  style?: React.CSSProperties
}) {
  const open = useOpenTrialModal()
  return (
    <button
      type="button"
      onClick={() => open(placement)}
      className="font-body inline-flex items-center justify-center"
      style={{
        backgroundColor: '#C8A84B',
        color: '#0A1628',
        fontWeight: 600,
        border: 'none',
        cursor: 'pointer',
        ...style,
      }}
    >
      {children}
    </button>
  )
}

// ─── SECTION 1: HERO ───────────────────────────────────────────────────────

function HeroSection() {
  return (
    <section
      className="relative overflow-hidden"
      style={{
        backgroundColor: '#0A1628',
        backgroundImage: [
          'radial-gradient(ellipse at 65% 40%, rgba(200,168,75,0.07) 0%, transparent 60%)',
          'linear-gradient(to right, rgba(200,168,75,0.025) 1px, transparent 1px)',
          'linear-gradient(to bottom, rgba(200,168,75,0.025) 1px, transparent 1px)',
        ].join(', '),
        backgroundSize: 'auto, 60px 60px, 60px 60px',
        padding: '56px 24px 80px',
      }}
    >
      <div
        className="relative mx-auto text-center"
        style={{ maxWidth: '760px' }}
      >
        {/* Badge */}
        <div
          className="inline-flex items-center"
          style={{
            gap: '10px',
            backgroundColor: 'rgba(200,168,75,0.1)',
            border: '1px solid rgba(200,168,75,0.3)',
            borderRadius: '20px',
            padding: '6px 16px',
            marginBottom: '20px',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: '7px',
              height: '7px',
              borderRadius: '50%',
              backgroundColor: '#C8A84B',
              animation: 'fyspPulse 2s infinite ease-in-out',
            }}
          />
          <span
            className="font-body font-semibold uppercase"
            style={{
              fontSize: '11px',
              color: '#C8A84B',
              letterSpacing: '1.5px',
            }}
          >
            Horse Behavior · Rider Confidence · Clearer Communication
          </span>
        </div>

        <h1
          className="font-heading font-semibold"
          style={{
            fontSize: 'clamp(44px, 8vw, 72px)',
            color: '#FFFFFF',
            lineHeight: 1.05,
            marginBottom: '18px',
          }}
        >
          Find the real reason you and your horse
          {' '}
          <span style={{ fontStyle: 'italic', color: '#C8A84B' }}>
            feel stuck.
          </span>
        </h1>

        <p
          className="font-body mx-auto"
          style={{
            fontSize: '17px',
            color: 'rgba(255,255,255,0.6)',
            lineHeight: 1.7,
            maxWidth: '540px',
            marginBottom: '24px',
          }}
        >
          Whether you are dealing with fear, a behavior problem, mixed signals,
          or no clear training plan, Scott’s Four Factors help you identify where
          the breakdown begins and what kind of support fits next.
        </p>

        <a
          href="#horse-rider-assessment"
          onClick={() => startHomepageAssessment('homepage_hero')}
          className="font-body inline-flex items-center justify-center"
          style={{
            backgroundColor: '#C8A84B',
            color: '#0A1628',
            fontWeight: 600,
            fontSize: '15px',
            padding: '18px 48px',
            borderRadius: '12px',
            minHeight: '52px',
          }}
        >
          Find My Starting Point
        </a>

        <p
          className="font-body"
          style={{
            fontSize: '12px',
            color: 'rgba(255,255,255,0.4)',
            marginTop: '16px',
          }}
        >
          Four questions. Immediate guidance. No email required.
        </p>

        <div
          className="ah-hero-recognition"
          aria-label="Common reasons riders come to Advantage Horsemanship"
          style={{ marginTop: '28px' }}
        >
          {[
            'I am losing confidence',
            'My horse’s behavior changed',
            'We are not communicating',
            'I do not know what to work on',
          ].map((reason) => (
            <div
              key={reason}
              className="font-body flex items-center text-left"
              style={{
                gap: '10px',
                minHeight: '48px',
                padding: '11px 14px',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: '12px',
                backgroundColor: 'rgba(255,255,255,0.04)',
                color: 'rgba(255,255,255,0.78)',
                fontSize: '13px',
              }}
            >
              <span aria-hidden="true" style={{ color: '#C8A84B', fontWeight: 700 }}>✓</span>
              <span>{reason}</span>
            </div>
          ))}
        </div>

        {/* Stats row */}
        <div
          style={{
            marginTop: '64px',
            paddingTop: '40px',
            borderTop: '1px solid rgba(255,255,255,0.08)',
          }}
          className="flex flex-wrap items-center justify-center"
        >
          {[
            { value: '60,000+', label: 'Riders Helped' },
            { value: '20+', label: 'Years Coaching' },
            { value: '4 Factors', label: 'One Clear Starting Point' },
          ].map(({ value, label }) => (
            <div
              key={label}
              style={{ padding: '0 28px' }}
              className="text-center"
            >
              <div
                className="font-heading"
                style={{
                  fontSize: '38px',
                  color: '#C8A84B',
                  fontWeight: 600,
                  lineHeight: 1,
                  marginBottom: '8px',
                }}
              >
                {value}
              </div>
              <div
                className="font-body uppercase"
                style={{
                  fontSize: '10px',
                  letterSpacing: '1.5px',
                  color: 'rgba(255,255,255,0.35)',
                }}
              >
                {label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ─── SECTION 2: PROBLEM ───────────────────────────────────────────────────

function ProblemSection() {
  const cards = [
    {
      title: 'You freeze up when things go wrong',
      body: 'Confidence disappearing is a clue. The first step is separating the human response from the horse, connection, and environment around it.',
    },
    {
      title: 'Your horse is unpredictable or not listening',
      body: 'The visible behavior is the end of a chain. Finding the first change tells you whether to address understanding, regulation, timing, or context.',
    },
    {
      title: 'You have tried things but nothing sticks',
      body: 'Random advice creates random results. You need one clear priority and the right kind of support for the problem in front of you.',
    },
  ]

  return (
    <section
      style={{ backgroundColor: '#06090F', padding: '80px 24px' }}
    >
      <div
        className="mx-auto grid grid-cols-1 md:grid-cols-2 items-center"
        style={{ maxWidth: '1100px', gap: '72px' }}
      >
        <div>
          <Eyebrow>Sound familiar?</Eyebrow>
          <h2
            className="font-heading font-semibold leading-tight"
            style={{
              fontSize: 'clamp(32px, 5vw, 44px)',
              color: '#FFFFFF',
              marginBottom: '20px',
            }}
          >
            Most riders are working on the{' '}
            <span style={{ fontStyle: 'italic', color: '#C8A84B' }}>
              wrong thing.
            </span>
          </h2>
          <p
            className="font-body"
            style={{
              fontSize: '15px',
              color: 'rgba(255,255,255,0.55)',
              lineHeight: 1.7,
              marginBottom: '24px',
            }}
          >
            They focus on the horse's behavior when the real issue may be the
            horse, the human, the connection, or the environment. Start by
            identifying which factor is actually driving the pattern.
          </p>
          <a
            href="#horse-rider-assessment"
            onClick={() => startHomepageAssessment('homepage_problem')}
            className="font-body inline-flex items-center justify-center"
            style={{
              backgroundColor: '#C8A84B',
              color: '#0A1628',
              fontWeight: 600,
              fontSize: '13px',
              padding: '13px 30px',
              borderRadius: '6px',
              minHeight: '52px',
            }}
          >
            Find My Starting Point
          </a>
        </div>

        <div className="flex flex-col" style={{ gap: '14px' }}>
          {cards.map((c) => (
            <div
              key={c.title}
              style={{
                backgroundColor: 'rgba(255,255,255,0.03)',
                borderLeft: '3px solid #C8A84B',
                borderRadius: '0 10px 10px 0',
                padding: '20px 24px',
              }}
            >
              <h3
                className="font-heading font-semibold"
                style={{
                  fontSize: '20px',
                  color: '#FFFFFF',
                  marginBottom: '8px',
                  lineHeight: 1.3,
                }}
              >
                {c.title}
              </h3>
              <p
                className="font-body"
                style={{
                  fontSize: '13px',
                  color: 'rgba(255,255,255,0.45)',
                  lineHeight: 1.6,
                }}
              >
                {c.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ─── SECTION 3: THREE APP FEATURES ────────────────────────────────────────

const PILLAR_STYLES = `
  .pillar-card {
    background-color: rgba(255,255,255,0.03);
    border: 1px solid rgba(200,168,75,0.15);
    transition: background-color 0.22s ease, border-color 0.22s ease, transform 0.22s ease;
  }
  .pillar-card:hover {
    background-color: rgba(200,168,75,0.06);
    border-color: rgba(200,168,75,0.35);
    transform: translateY(-4px);
  }
`

type Pillar = {
  number: string
  color: string
  icon: ReactNode
  title: string
  description: string
  exampleLabel: string
  exampleText: string
}

function PillarsSection() {
  const pillars: Pillar[] = [
    {
      number: '01',
      color: '#C8A84B',
      icon: (
        <svg
          width="28"
          height="28"
          viewBox="0 0 28 28"
          fill="none"
          stroke="#C8A84B"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M4 6h20M4 11h20M4 16h14M4 21h10" />
          <circle cx="22" cy="21" r="3" />
          <path d="M22 21v-2M22 22v.5" />
        </svg>
      ),
      title: 'Ask Coach Scott Anything',
      description:
        'Get direct answers to your specific horse and rider questions without searching YouTube or digging through a video library. Coach Scott is trained on Scott\'s full methodology and gives you answers personalized to your situation.',
      exampleLabel: 'Example question',
      exampleText:
        'My mare pins her ears every time I ask for the canter. I have tried moving her forward first but nothing changes. What am I missing?',
    },
    {
      number: '02',
      color: '#7EB8E0',
      icon: (
        <svg
          width="28"
          height="28"
          viewBox="0 0 28 28"
          fill="none"
          stroke="#7EB8E0"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M14 3l10 6v10l-10 6-10-6V9l10-6z" />
          <path d="M14 9l5 3v4l-5 3-5-3v-4l5-3z" />
        </svg>
      ),
      title: 'Your Goal-Driven Journey',
      description:
        'Coach Scott turns your larger goal into a personalized path of milestones and sessions. The journey continues for as long as the goal requires, adapting as you and your horse progress.',
      exampleLabel: 'What a step looks like',
      exampleText:
        "Current milestone: Softness and willing responses in hand. Today's session gives you the exercise, what to watch for, and the response that shows you are ready for the next step.",
    },
    {
      number: '03',
      color: '#5DCAA5',
      icon: (
        <svg
          width="28"
          height="28"
          viewBox="0 0 28 28"
          fill="none"
          stroke="#5DCAA5"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="14" cy="6" r="3" />
          <path d="M14 9v8" />
          <path d="M9 13l5-1.5 5 1.5" />
          <path d="M14 17l-4 8M14 17l4 8" />
        </svg>
      ),
      title: 'Guided Rides with Scott',
      description:
        'Pre-recorded guided rides where Scott coaches you through the session in real time as you ride. Different rides for different things you are working on. No more riding alone and wondering if you are doing it right.',
      exampleLabel: 'Example ride',
      exampleText:
        "Building Softness at the Walk. Scott narrates the full 20-minute session, telling you what to ask for, when to release, and how to read your horse's response.",
    },
  ]

  return (
    <section style={{ backgroundColor: '#0A1628', padding: '96px 24px' }}>
      <style>{PILLAR_STYLES}</style>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <div
          className="text-center mx-auto"
          style={{ maxWidth: '600px', marginBottom: '56px' }}
        >
          <Eyebrow>Inside Rider's Way</Eyebrow>
          <h2
            className="font-heading font-semibold leading-tight"
            style={{
              fontSize: 'clamp(32px, 5vw, 44px)',
              color: '#FFFFFF',
              marginBottom: '16px',
            }}
          >
            Three tools.{' '}
            <span style={{ fontStyle: 'italic', color: '#C8A84B' }}>
              One path forward.
            </span>
          </h2>
          <p
            className="font-body"
            style={{
              fontSize: '15px',
              color: 'rgba(255,255,255,0.55)',
              lineHeight: 1.7,
            }}
          >
            Once Rider's Way has helped you define the goal, these three tools
            keep each session connected to it and help you adjust when your
            horse gives you new information.
          </p>
        </div>

        <div
          className="grid grid-cols-1 md:grid-cols-3"
          style={{ gap: '24px' }}
        >
          {pillars.map((p) => (
            <PillarCard key={p.number} {...p} />
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: '48px' }}>
          <TrialButton
            placement="homepage_app_tools"
            style={{
              fontSize: '14px',
              padding: '16px 40px',
              borderRadius: '6px',
              minHeight: '52px',
            }}
          >
            Start a Rider's Way Trial
          </TrialButton>
          <p
            className="font-body"
            style={{
              fontSize: '11px',
              color: 'rgba(255,255,255,0.25)',
              marginTop: '14px',
            }}
          >
            Available on iOS and Android. $19.99/month after trial.
          </p>
        </div>
      </div>
    </section>
  )
}

function PillarCard({
  number,
  color,
  icon,
  title,
  description,
  exampleLabel,
  exampleText,
}: Pillar) {
  return (
    <div
      className="pillar-card relative flex flex-col"
      style={{
        borderRadius: '16px',
        padding: '36px 28px',
        paddingTop: '40px',
      }}
    >
      {/* 3px accent bar at top */}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          backgroundColor: color,
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px',
        }}
      />
      {/* Large number top left */}
      <span
        aria-hidden="true"
        className="font-heading"
        style={{
          position: 'absolute',
          top: '16px',
          left: '20px',
          fontSize: '56px',
          color: 'rgba(200,168,75,0.08)',
          lineHeight: 1,
          fontWeight: 600,
        }}
      >
        {number}
      </span>
      {/* Icon */}
      <div
        className="flex items-center justify-center"
        style={{
          width: '52px',
          height: '52px',
          borderRadius: '12px',
          backgroundColor: `${color}1a`,
          marginTop: '36px',
          marginBottom: '20px',
        }}
      >
        {icon}
      </div>
      <h3
        className="font-heading font-semibold leading-tight"
        style={{
          fontSize: '24px',
          color: '#FFFFFF',
          marginBottom: '12px',
        }}
      >
        {title}
      </h3>
      <p
        className="font-body"
        style={{
          fontSize: '13px',
          color: 'rgba(255,255,255,0.5)',
          lineHeight: 1.75,
          marginBottom: '20px',
          flex: 1,
        }}
      >
        {description}
      </p>
      <div
        style={{
          backgroundColor: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(200,168,75,0.1)',
          borderRadius: '8px',
          padding: '14px 16px',
        }}
      >
        <p
          className="font-body uppercase"
          style={{
            fontSize: '9px',
            letterSpacing: '1.5px',
            color: 'rgba(200,168,75,0.5)',
            marginBottom: '6px',
          }}
        >
          {exampleLabel}
        </p>
        <p
          className="font-body"
          style={{
            fontSize: '11px',
            color: 'rgba(255,255,255,0.5)',
            fontStyle: 'italic',
            lineHeight: 1.55,
          }}
        >
          {exampleText}
        </p>
      </div>
    </div>
  )
}

// ─── SECTION 4: PHONE SHOWCASE ────────────────────────────────────────────

function PhoneShowcaseSection() {
  return (
    <section style={{ backgroundColor: '#06090F', padding: '96px 24px' }}>
      <div
        className="mx-auto grid grid-cols-1 md:grid-cols-2 items-center"
        style={{ maxWidth: '1100px', gap: '80px' }}
      >
        <div className="flex justify-center">
          <PhoneMockup />
        </div>
        <div>
          <Eyebrow>One path: The Rider's Way App</Eyebrow>
          <h2
            className="font-heading font-semibold leading-tight"
            style={{
              fontSize: 'clamp(28px, 4.5vw, 36px)',
              color: '#FFFFFF',
              marginBottom: '20px',
            }}
          >
            A plan between lessons,{' '}
            <span style={{ fontStyle: 'italic', color: '#C8A84B' }}>
              wherever you ride.
            </span>
          </h2>
          <p
            className="font-body"
            style={{
              fontSize: '14px',
              color: 'rgba(255,255,255,0.55)',
              lineHeight: 1.75,
              marginBottom: '16px',
            }}
          >
            Rider's Way is the daily-support option inside Scott's system. It
            turns the goal you choose into milestones, then gives you the next
            useful session based on where you and your horse are now.
          </p>
          <p
            className="font-body"
            style={{
              fontSize: '14px',
              color: 'rgba(255,255,255,0.55)',
              lineHeight: 1.75,
              marginBottom: '28px',
            }}
          >
            It is built for riders who want direction between lessons: a plan
            at the barn, progress they can see, and Coach Scott available when
            something changes.
          </p>
          <div className="flex flex-col items-center gap-4">
            <TrialButton
              placement="homepage_app_showcase"
              style={{
                fontSize: '14px',
                padding: '16px 38px',
                borderRadius: '6px',
                minHeight: '52px',
                width: '100%',
              }}
            >
              Start a Rider's Way Trial
            </TrialButton>
            <AppBadges />
          </div>
        </div>
      </div>
    </section>
  )
}

function PhoneMockup() {
  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '24px 20px',
      }}
    >
      {/* Gold radial glow */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          width: '360px',
          height: '540px',
          borderRadius: '50%',
          background:
            'radial-gradient(ellipse at center, rgba(200,168,75,0.18) 0%, transparent 70%)',
          pointerEvents: 'none',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      />

      <img
        src={assets.app.homeCurrent}
        alt="Current Rider's Way home screen showing today's session and goal journey progress"
        width={900}
        height={1502}
        loading="lazy"
        style={{
          position: 'relative',
          width: 'min(330px, 100%)',
          height: 'auto',
          display: 'block',
          filter: 'drop-shadow(0 30px 50px rgba(0,0,0,0.55))',
        }}
      />

    </div>
  )
}

// ─── SECTION 5: TESTIMONIALS ──────────────────────────────────────────────

type Quote = {
  initials: string
  name: string
  source: string
  quote: string
}

const QUOTES: Quote[] = [
  {
    initials: 'RS',
    name: 'Rose S.',
    source: 'Scott Purdum student',
    quote:
      'I felt strangely fear-free as I got on. The truck was dropping off a dumpster right across the fence. I was amazingly fear free for the whole ride. That is a huge win.',
  },
  {
    initials: 'AA',
    name: 'Ali A.',
    source: 'Scott Purdum student',
    quote:
      'I managed to almost fully control Querida just through my presence, without any sound or pull of the rope. That got my confidence even higher and I dared to hack her out for 25 minutes.',
  },
  {
    initials: 'AA',
    name: 'Ali A.',
    source: 'Scott Purdum student',
    quote:
      'I lost every single bit of my confidence. Today I decided to break that barrier. I used the breathing technique we learned and it really helped. Scott, you are my hero.',
  },
  {
    initials: 'LT',
    name: 'Lisa T.',
    source: 'Scott Purdum student',
    quote:
      'I asked him to canter and he responded. We did our warm up, trotted, did patterns. Then I went for the canter. And we did, both ways. I loved it.',
  },
]

function TestimonialsSection() {
  return (
    <section
      style={{ backgroundColor: '#F7F4EF', padding: '96px 24px' }}
    >
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <div className="text-center" style={{ marginBottom: '48px' }}>
          <p
            className="font-body font-semibold uppercase"
            style={{
              fontSize: '11px',
              letterSpacing: '3px',
              color: '#C8A84B',
              marginBottom: '16px',
            }}
          >
            Results from riders Scott has coached
          </p>
          <h2
            className="font-heading font-semibold leading-tight"
            style={{
              fontSize: 'clamp(32px, 5vw, 44px)',
              color: '#0A1628',
            }}
          >
            Riders who figured it out.
          </h2>
        </div>
        <div
          className="grid grid-cols-1 md:grid-cols-2"
          style={{ gap: '20px' }}
        >
          {QUOTES.map((q, i) => (
            <QuoteCard key={i} {...q} />
          ))}
        </div>
      </div>
    </section>
  )
}

function QuoteCard({ initials, name, source, quote }: Quote) {
  return (
    <figure
      style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '14px',
        border: '1px solid rgba(10,22,40,0.07)',
        padding: '28px',
      }}
    >
      <span
        aria-hidden="true"
        className="font-heading italic block leading-none select-none"
        style={{
          fontSize: '44px',
          color: 'rgba(200,168,75,0.45)',
          marginBottom: '8px',
        }}
      >
        &ldquo;
      </span>
      <blockquote
        className="font-body italic"
        style={{
          fontSize: '14px',
          color: '#2A3A50',
          lineHeight: 1.75,
          marginBottom: '18px',
        }}
      >
        {quote}
      </blockquote>
      <hr
        style={{
          border: 'none',
          borderTop: '1px solid rgba(10,22,40,0.07)',
          margin: '0 0 14px',
        }}
      />
      <figcaption className="flex items-center" style={{ gap: '12px' }}>
        <div
          className="flex items-center justify-center flex-shrink-0 rounded-full"
          style={{
            width: '36px',
            height: '36px',
            backgroundColor: '#0A1628',
          }}
        >
          <span
            className="font-body font-bold"
            style={{ fontSize: '12px', color: '#C8A84B' }}
          >
            {initials}
          </span>
        </div>
        <div>
          <p
            className="font-body font-bold"
            style={{ fontSize: '12px', color: '#0A1628', lineHeight: 1.3 }}
          >
            {name}
          </p>
          <p
            className="font-body"
            style={{
              fontSize: '10px',
              color: 'rgba(10,22,40,0.4)',
              lineHeight: 1.3,
            }}
          >
            {source}
          </p>
        </div>
      </figcaption>
    </figure>
  )
}

// ─── SECTION 6: ABOUT SCOTT ───────────────────────────────────────────────

function AboutScottSection() {
  return (
    <section style={{ backgroundColor: '#0A1628', padding: '96px 24px' }}>
      <div
        className="mx-auto grid grid-cols-1 md:grid-cols-2 items-center"
        style={{ maxWidth: '1100px', gap: '80px' }}
      >
        <div className="relative" style={{ aspectRatio: '4 / 5' }}>
          <img
            src={assets.scott.groundworkHaltering}
            srcSet={assets.scott.groundworkHalteringSrcSet}
            sizes="(min-width: 1024px) 960px, 100vw"
            alt="Scott Purdum doing groundwork with a chestnut horse"
            width={800}
            height={1000}
            loading="lazy"
            className="w-full h-full"
            style={{
              objectFit: 'cover',
              borderRadius: '16px',
              border: '1px solid rgba(200,168,75,0.2)',
            }}
          />
          <span
            className="font-body font-semibold"
            style={{
              position: 'absolute',
              bottom: '-16px',
              right: '-16px',
              backgroundColor: '#C8A84B',
              color: '#0A1628',
              fontSize: '11px',
              padding: '12px 20px',
              borderRadius: '8px',
              whiteSpace: 'nowrap',
            }}
          >
            20+ Years. 60,000 Riders.
          </span>
        </div>
        <div>
          <Eyebrow>About Scott Purdum</Eyebrow>
          <h2
            className="font-heading font-semibold leading-tight"
            style={{
              fontSize: 'clamp(32px, 5vw, 44px)',
              color: '#FFFFFF',
              marginBottom: '20px',
            }}
          >
            Horse Behaviorist and{' '}
            <span style={{ fontStyle: 'italic', color: '#C8A84B' }}>
              Rider Confidence Coach
            </span>
          </h2>
          <p
            className="font-body"
            style={{
              fontSize: '15px',
              color: 'rgba(255,255,255,0.6)',
              lineHeight: 1.75,
              marginBottom: '16px',
            }}
          >
            Scott has spent over two decades helping riders stop blaming their
            horses and start building real partnerships. His Four Factors
            framework identifies exactly where the breakdown is happening so
            you fix the right thing instead of spinning your wheels.
          </p>
          <p
            className="font-body"
            style={{
              fontSize: '15px',
              color: 'rgba(255,255,255,0.6)',
              lineHeight: 1.75,
              marginBottom: '24px',
            }}
          >
            Every Advantage Horsemanship path uses that same methodology,
            whether you learn through a self-paced program, join live coaching,
            work with Scott directly, or carry a plan with you to the barn.
          </p>
          <Link
            to="/about"
            className="font-body font-semibold"
            style={{
              fontSize: '14px',
              color: '#C8A84B',
              borderBottom: '1px solid #C8A84B',
              paddingBottom: '2px',
            }}
          >
            Learn Scott's story &rarr;
          </Link>
        </div>
      </div>
    </section>
  )
}

// ─── SECTION 7: READY TO GO DEEPER ────────────────────────────────────────

const DEEPER_STYLES = `
  .deeper-card {
    background-color: rgba(255,255,255,0.03);
    border: 1px solid rgba(200,168,75,0.12);
    transition: background-color 0.22s ease, border-color 0.22s ease;
  }
  .deeper-card:hover {
    background-color: rgba(200,168,75,0.05);
    border-color: rgba(200,168,75,0.35);
  }
`

function ReadyDeeperSection() {
  const cards = [
    {
      title: 'I Need a Plan at the Barn',
      desc:
        'For ongoing direction that changes with your horse and your goal. Get a clear next session and access to Coach Scott between rides.',
      linkText: "Explore Rider's Way",
      to: 'https://riderswayapp.com',
    },
    {
      title: 'I Want to Build My Confidence',
      desc:
        'Live online confidence coaching for riders who hesitate, freeze, or lose trust in their decisions. Get weekly feedback and accountability from Scott.',
      linkText: 'Explore Confidence Coaching',
      to: '/accelerator',
    },
    {
      title: 'I Need Help With Training Issues',
      desc:
        'Live technical coaching for riders who need to understand behavior, communication, and why a training pattern keeps returning.',
      linkText: 'Explore Horse Sense',
      to: '/horse-sense',
    },
    {
      title: 'I Want to Work Directly With Scott',
      desc:
        'For complex or highly specific situations. Choose a Riding Intensive in person or ask about private Zoom coaching from anywhere in the world.',
      linkText: 'Explore Private Coaching',
      to: '/private-sessions',
    },
  ]

  return (
    <section style={{ backgroundColor: '#06090F', padding: '80px 40px' }}>
      <style>{DEEPER_STYLES}</style>
      <div style={{ maxWidth: '1120px', margin: '0 auto' }}>
        <div
          className="text-center mx-auto"
          style={{ maxWidth: '500px' }}
        >
          <Eyebrow>Your result does the sorting</Eyebrow>
          <h2
            className="font-heading font-semibold leading-tight"
            style={{
              fontSize: 'clamp(28px, 4vw, 36px)',
              color: '#FFFFFF',
              marginBottom: '16px',
            }}
          >
            One starting point. The right kind of help.
          </h2>
          <p
            className="font-body"
            style={{
              fontSize: '14px',
              color: 'rgba(255,255,255,0.45)',
              lineHeight: 1.7,
            }}
          >
            You do not need to compare every offer on your own. Your answers
            help separate the problem you are facing from the way you will
            realistically use support.
          </p>
        </div>

        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
          style={{ gap: '16px', marginTop: '40px' }}
        >
          {cards.map((c) => (
            <Link
              key={c.title}
              to={c.to}
              onClick={() => logGaEvent('support_path_click', {
                path_name: c.title,
                destination: c.to,
                placement: 'homepage_support_paths',
              })}
              className="deeper-card block"
              style={{
                borderRadius: '12px',
                padding: '32px 28px',
                textAlign: 'left',
              }}
            >
              <h3
                className="font-heading font-semibold leading-tight"
                style={{
                  fontSize: '20px',
                  color: '#FFFFFF',
                  marginBottom: '10px',
                }}
              >
                {c.title}
              </h3>
              <p
                className="font-body text-sm mt-3"
                style={{
                  color: 'rgba(255,255,255,0.4)',
                  lineHeight: 1.6,
                  marginBottom: '16px',
                }}
              >
                {c.desc}
              </p>
              <span
                className="font-body font-semibold text-sm mt-4 inline-block"
                style={{
                  color: '#C8A84B',
                  letterSpacing: '0.5px',
                }}
              >
                {c.linkText} &rarr;
              </span>
            </Link>
          ))}
        </div>

        <p
          className="font-body text-center"
          style={{
            fontSize: '11px',
            color: 'rgba(255,255,255,0.2)',
            marginTop: '28px',
          }}
        >
          Prefer to work independently? The assessment can still match you to the
          most relevant self-paced program without adding another choice here.
        </p>
      </div>
    </section>
  )
}

// ─── SECTION 8: REPEAT CTA STRIP ──────────────────────────────────────────

function RepeatCtaSection() {
  return (
    <section
      className="relative overflow-hidden"
      style={{ padding: '72px 40px', textAlign: 'center' }}
    >
      <img
        src={assets.scott.ridingChestnutCanter}
        srcSet={assets.scott.ridingChestnutCanterSrcSet}
        sizes="100vw"
        alt=""
        aria-hidden="true"
        width={940}
        height={788}
        loading="lazy"
        className="absolute inset-0 w-full h-full"
        style={{ objectFit: 'cover', objectPosition: 'center' }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{ backgroundColor: 'rgba(200,168,75,0.88)' }}
      />
      <div className="relative mx-auto" style={{ maxWidth: '720px' }}>
        <h2
          className="font-heading font-semibold leading-tight"
          style={{
            fontSize: 'clamp(32px, 5.5vw, 46px)',
            color: '#0A1628',
            marginBottom: '14px',
          }}
        >
          Not sure which path fits?
        </h2>
        <p
          className="font-body"
          style={{
            fontSize: '15px',
            color: 'rgba(10,22,40,0.6)',
            marginBottom: '28px',
          }}
        >
          Let the assessment identify your clearest starting point and match
          you with the kind of support you will actually use.
        </p>
        <a
          href="#horse-rider-assessment"
          onClick={() => startHomepageAssessment('homepage_final')}
          className="font-body inline-flex items-center justify-center"
          style={{
            backgroundColor: '#0A1628',
            color: '#C8A84B',
            fontWeight: 600,
            fontSize: '14px',
            padding: '17px 44px',
            borderRadius: '6px',
            minHeight: '52px',
            border: 'none',
          }}
        >
          Find My Starting Point
        </a>
        <p
          className="font-body"
          style={{
            fontSize: '12px',
            color: 'rgba(10,22,40,0.4)',
            marginTop: '14px',
          }}
        >
          Four questions. Immediate guidance. No email required.
        </p>
        <Link
          to="/programs"
          className="font-body inline-block font-semibold"
          style={{ color: '#0A1628', fontSize: '13px', marginTop: '18px', textDecoration: 'underline', textUnderlineOffset: '4px' }}
        >
          Or explore all programs
        </Link>
      </div>
    </section>
  )
}

// ─── SECTION 9: EMAIL CAPTURE ─────────────────────────────────────────────

function EmailCaptureSection() {
  const [firstName, setFirstName] = useState('')
  const [email, setEmail] = useState('')
  const [honeypotValue, setHoneypotValue] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const savedData = useRef<{ firstName: string; email: string } | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (status === 'loading') return

    savedData.current = { firstName, email }
    setStatus('loading')
    setErrorMessage(null)

    try {
      const res = await fetch('/api/submitForm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          formType: 'homepage_email',
          payload: {
            first_name: firstName,
            email,
          },
          honeypot: honeypotValue,
        }),
      })

      if (!res.ok) {
        try {
          const data = (await res.json()) as { message?: string }
          if (typeof data.message === 'string' && data.message.length > 0) {
            setErrorMessage(data.message)
          }
        } catch {
          // Body unparseable — fall through to generic error.
        }
        setStatus('error')
        return
      }
      setStatus('success')
      // The homepage email capture is the site's highest volume lead source and
      // fired NO analytics event until 2026-08-10, which is why "Lead captured"
      // in GA only ever counted the contact form. Inline success, no redirect,
      // so the event fires here. Must be marked as a key event in GA Admin.
      logGaEvent('generate_lead', { form: 'homepage_email' })
    } catch {
      setStatus('error')
    }
  }

  const handleRetry = () => {
    if (!savedData.current) return
    setFirstName(savedData.current.firstName)
    setEmail(savedData.current.email)
    setErrorMessage(null)
    setStatus('idle')
  }

  const sectionStyle = {
    backgroundColor: '#0A1628',
    borderTop: '1px solid rgba(200,168,75,0.1)',
    padding: '80px 24px',
  } as const

  if (status === 'success') {
    return (
      <section style={sectionStyle} className="text-center">
        <div className="max-w-xl mx-auto">
          <p className="font-body text-lg font-semibold" style={{ color: '#C8A84B' }}>
            You are on the list. Check your inbox.
          </p>
        </div>
      </section>
    )
  }

  return (
    <section style={sectionStyle}>
      <div className="max-w-xl mx-auto text-center">
        <h2
          className="font-heading font-semibold"
          style={{
            fontSize: 'clamp(26px, 4vw, 34px)',
            color: '#FFFFFF',
            marginBottom: '12px',
          }}
        >
          Get weekly horse sense from Scott.
        </h2>
        <p
          className="font-body"
          style={{
            fontSize: '15px',
            color: 'rgba(255,255,255,0.55)',
            marginBottom: '28px',
          }}
        >
          No fluff. Practical communication and confidence tips every week.
        </p>

        {status === 'error' && (
          <p
            className="font-body"
            style={{ fontSize: '13px', color: '#ff6b6b', marginBottom: '14px' }}
          >
            {errorMessage ?? 'Something went wrong.'}{' '}
            <button
              onClick={handleRetry}
              className="underline font-semibold hover:opacity-80 transition-opacity"
              style={{ color: '#ff6b6b' }}
            >
              Try again
            </button>
          </p>
        )}

        <form onSubmit={handleSubmit} noValidate className="flex flex-col" style={{ gap: '12px' }}>
          {/* Honeypot — bots fill this, humans never see it. */}
          <input
            type="text"
            name="website"
            value={honeypotValue}
            onChange={(e) => setHoneypotValue(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: '-9999px',
              width: '1px',
              height: '1px',
              overflow: 'hidden',
            }}
          />
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="First name"
            required
            aria-label="First name"
            className="font-body w-full"
            style={{
              fontSize: '16px',
              color: '#FFFFFF',
              backgroundColor: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(200,168,75,0.2)',
              borderRadius: '6px',
              padding: '14px 16px',
              minHeight: '52px',
              outline: 'none',
            }}
          />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email address"
            required
            aria-label="Email address"
            className="font-body w-full"
            style={{
              fontSize: '16px',
              color: '#FFFFFF',
              backgroundColor: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(200,168,75,0.2)',
              borderRadius: '6px',
              padding: '14px 16px',
              minHeight: '52px',
              outline: 'none',
            }}
          />
          <button
            type="submit"
            disabled={status === 'loading' || !firstName.trim() || !email.trim()}
            style={{
              backgroundColor: '#C8A84B',
              color: '#0A1628',
              fontWeight: 600,
              padding: '16px 32px',
              fontSize: '16px',
              border: 'none',
              cursor: 'pointer',
              width: '100%',
              borderRadius: '6px',
              minHeight: '52px',
            }}
          >
            {status === 'loading' ? 'Sending...' : 'Send Me the Tips'}
          </button>
        </form>
      </div>
    </section>
  )
}

// ─── SHARED ────────────────────────────────────────────────────────────────

function AppBadges() {
  return (
    <div className="flex flex-row justify-center items-center gap-3">
      <a
        href="https://apps.apple.com/us/app/riders-way/id6752657327"
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => logGaEvent('store_click', { store: 'app_store', placement: 'homepage_app_showcase' })}
      >
        <img
          src={assets.app.badgeAppStoreWhite}
          alt="Download on the App Store"
          width={120}
          height={40}
          className="h-9 w-auto min-w-[135px] object-contain"
        />
      </a>
      <a
        href="https://play.google.com/store/apps/details?id=com.coachscott.app"
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => logGaEvent('store_click', { store: 'google_play', placement: 'homepage_app_showcase' })}
      >
        <img
          src={assets.app.badgeGooglePlay}
          alt="Get it on Google Play"
          width={478}
          height={142}
          className="h-9 w-auto"
        />
      </a>
    </div>
  )
}

function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <p
      className="font-body font-semibold uppercase"
      style={{
        fontSize: '11px',
        letterSpacing: '3px',
        color: '#C8A84B',
        marginBottom: '16px',
      }}
    >
      {children}
    </p>
  )
}
